import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, fmtNum, today } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import Can from '../../components/Can';
import './Master.css';

const TABS = ['Products', 'Tanks', 'Pumps', 'Nozzles', 'Customers', 'Suppliers'];

/* ═══════════ TANK LEDGER — transaction-level, below the Tank Master table ═══════════
   System-generated & read-only. Built from source tables by tank_id (purchases =
   Credit/Stock In; metered dispensing/testing/evap = Debit/Stock Out).
   Running Balance = Opening + Purchases − Sales − Testing − Evaporation.
   Variance (Physical Dip − Closing) is a separate field, not in the balance. */
const TANK_TXN_TYPES = ['All', 'PURCHASE', 'SALE', 'TESTING', 'EVAP'];

function TankLedger({ tanks }) {
  const { t: tr } = useTranslation();
  const tankList = tanks || [];
  const [tankId, setTankId] = useState('');
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [type, setType] = useState('All');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Default to the first tank once the list arrives.
  useEffect(() => {
    if (!tankId && tankList.length) setTankId(String(tankList[0].id));
  }, [tankList, tankId]);

  useEffect(() => {
    if (!tankId) return;
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await api.stockLedger.tankLedger(tankId, from, to, type === 'All' ? '' : type);
        if (alive) setData(res);
      } catch (e) {
        if (alive) setError(e.message || tr('master.tankLedger.loadFailed'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tankId, from, to, type]);

  // `get()` already unwraps the {success,data} envelope, so `data` IS the
  // payload object. Be tolerant of both shapes the backend may return:
  //   • flat:   { rows, opening_balance, total_debit, total_credit, closing_balance, variance, physical_dip }
  //   • nested: { rows, totals: { opening|opening_balance, debit|total_debit,
  //               credit|total_credit, closing|closing_balance, variance } }
  const d = data || {};
  const t = d.totals || d;
  const num = (v) => Number(v ?? 0) || 0;
  const openingStock = num(d.opening_stock ?? t.opening_stock);
  const openingStockDate = d.opening_stock_date ?? t.opening_stock_date ?? null;
  const opening      = num(d.opening_balance ?? t.opening_balance ?? t.opening);
  const totalDebit  = num(d.total_debit ?? t.total_debit ?? t.debit ?? t.total_sales);
  const totalCredit = num(d.total_credit ?? t.total_credit ?? t.credit ?? t.purchases);
  const closing     = num(d.closing_balance ?? t.closing_balance ?? t.closing);
  const variance    = d.variance ?? t.variance ?? null;
  const physicalDip = d.physical_dip ?? t.physical_dip ?? null;
  const rows = (d.rows || []).map(r => ({
    date:            r.date ?? r.entry_date ?? r.txn_date ?? '',
    reference_no:    r.reference_no ?? r.ref_no ?? r.reference ?? '—',
    type:            r.type ?? r.transaction_type ?? r.txn_type ?? '',
    debit_qty:       num(r.debit_qty ?? r.debit),
    credit_qty:      num(r.credit_qty ?? r.credit),
    running_balance: num(r.running_balance ?? r.running ?? r.balance),
    remarks:         r.remarks ?? r.remark ?? r.narration ?? '',
  }));

  // Type filter is applied CLIENT-SIDE so it's instant and works regardless of
  // whether the backend honours the `type` param. Running balance / totals stay
  // computed over the full period (Closing must equal the true tank stock), so
  // only the visible rows change — the summary numbers intentionally do not.
  const TYPE_FILTER = { PURCHASE: 'PURCHASE', SALE: 'SALE', TESTING: 'TESTING LOSS', EVAP: 'EVAP LOSS' };
  const displayRows = type === 'All' ? rows : rows.filter(r => r.type === TYPE_FILTER[type]);

  return (
    <div className="section-card" style={{ marginTop: 16 }}>
      <div className="section-header">{tr('master.tankLedger.title')}</div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: 12, alignItems: 'flex-end' }}>
        <div className="form-group">
          <label>{tr('common.tank')}</label>
          <select className="form-control" value={tankId} onChange={e => setTankId(e.target.value)}>
            {tankList.length === 0 && <option value="">{tr('master.tankLedger.noTanks')}</option>}
            {tankList.map(t => <option key={t.id} value={t.id}>{t.tank_no} ({t.product_code})</option>)}
          </select>
        </div>
        <div className="form-group"><label>{tr('common.from')}</label><input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div className="form-group"><label>{tr('common.to')}</label><input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} /></div>
        <div className="form-group">
          <label>{tr('master.tankLedger.transactionType')}</label>
          <select className="form-control" value={type} onChange={e => setType(e.target.value)}>
            {TANK_TXN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading ? <Spinner /> : (
        <>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{tr('common.date')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('master.tankLedger.referenceNo')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('master.tankLedger.transactionType')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('master.tankLedger.debitQty')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('master.tankLedger.creditQty')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('master.tankLedger.runningBalance')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.remarks')}</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ fontWeight: 600, background: '#f5f9fc' }}>
                  <td colSpan={5}>{tr('master.tankLedger.openingBalance')}</td>
                  <td className="right-align">{fmtNum(opening)}</td>
                  <td></td>
                </tr>
                {displayRows.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('master.tankLedger.noMovements')}</td></tr>
                ) : displayRows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.date}</td>
                    <td>{r.reference_no}</td>
                    <td><span className={`badge ${Number(r.credit_qty) > 0 ? 'badge-success' : 'badge-danger'}`}>{r.type}</span></td>
                    <td className="right-align">{Number(r.debit_qty) > 0 ? fmtNum(r.debit_qty) : '—'}</td>
                    <td className="right-align">{Number(r.credit_qty) > 0 ? fmtNum(r.credit_qty) : '—'}</td>
                    <td className="right-align" style={{ fontWeight: 700 }}>{fmtNum(r.running_balance)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.remarks}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, background: '#f5f9fc' }}>
                  <td colSpan={3} style={{ textAlign: 'right' }}>{tr('master.tankLedger.totals')}</td>
                  <td className="right-align">{fmtNum(totalDebit)}</td>
                  <td className="right-align">{fmtNum(totalCredit)}</td>
                  <td className="right-align">{fmtNum(closing)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '10px 14px', fontSize: 13 }}>
            <span><strong>{tr('master.tankLedger.openingStock')}:</strong> {fmtNum(openingStock)} L</span>
            {openingStockDate && <span><strong>{tr('master.tankLedger.openingStockDate')}:</strong> {openingStockDate}</span>}
            <span><strong>{tr('master.tankLedger.openingBalance')}:</strong> {fmtNum(opening)} L</span>
            <span><strong>{tr('master.tankLedger.totalDebit')}:</strong> {fmtNum(totalDebit)} L</span>
            <span><strong>{tr('master.tankLedger.totalCredit')}:</strong> {fmtNum(totalCredit)} L</span>
            <span><strong>{tr('master.tankLedger.closingBalance')}:</strong> {fmtNum(closing)} L</span>
            <span style={{ color: variance == null ? 'var(--text-muted)' : (Number(variance) === 0 ? 'var(--secondary)' : 'var(--danger)') }}>
              <strong>{tr('master.tankLedger.variance')}:</strong>{' '}
              {variance == null
                ? tr('master.tankLedger.noPhysicalDip')
                : `${fmtNum(variance)} L${physicalDip != null ? tr('master.tankLedger.physicalDipDetail', { dip: fmtNum(physicalDip), closing: fmtNum(closing) }) : ''}`}
            </span>
          </div>
          <div style={{ padding: '0 14px 12px', fontSize: 11, color: 'var(--text-muted)' }}>
            {tr('master.tankLedger.footnote')}
          </div>
        </>
      )}
    </div>
  );
}

const EMPTY_P = { code: '', name: '', hsn_code: '', unit: 'Ltr', tax_per: 0, rate: 0, type: 'Lubricant' };
const EMPTY_T = { tank_no: '', product_code: '', capacity: '', current_stock: '', opening_stock_date: '', min_level: '' };
const EMPTY_PU = { pump_no: '', name: '', product_code: '', nozzles: 2, status: 'Active', last_reading: 0 };

const EMPTY_NO = { nozzle_no: '', pump_id: '', tank_id: '' };
const EMPTY_C = { name: '', gstin: '', credit_limit: 0, balance: 0, type: 'Cash', phone: '', address: '' };
const EMPTY_S = { name: '', gstin: '', contact: '', product: '' };

export default function Master({ subPage = "Products" }) {
  const { t: tr } = useTranslation();
  const [tab, setTab] = useState(subPage);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [confirm, setConfirm] = useState(null);
  useEffect(() => { setTab(subPage); setShowForm(false); setEditRow(null); }, [subPage]);
  const [msg, setMsg] = useState('');

  const products = useApi(() => api.products.list(), []);
  const tanks = useApi(() => api.tanks.list(true), []);
  // Tank Master shows a tank only when it's Active OR has transaction history.
  // A deleted/empty tank (is_active=0 with no meter readings or transactions)
  // is a discarded mistake and is hidden entirely; inactive tanks that DO have
  // history stay visible so they can be reviewed or reactivated.
  const visibleTanks = (tanks.data || []).filter(
    t => Number(t.is_active ?? 1) === 1 || Number(t.has_transactions)
  );
  const pumps = useApi(() => api.pumps.list(), []);
  // Active-only: deleted (deactivated) nozzles are hidden from the Nozzle Master,
  // matching the backend GET default. Drop the `all` flag to exclude Inactive rows.
  const nozzles = useApi(() => api.nozzles.list(), []);
  const customers = useApi(() => api.customers.list(), []);
  const suppliers = useApi(() => api.suppliers.list(), []);
  const [employees, setEmployees] = useState([]);
  const [historyCode, setHistoryCode] = useState(null);
  const [confirmDeleteRate, setConfirmDeleteRate] = useState(null);
  const [confirmNozzle, setConfirmNozzle] = useState(null);

  const [pForm, setPForm] = useState(EMPTY_P);
  const [tForm, setTForm] = useState(EMPTY_T);
  // Meter Start Date — used to default the tank "Opening Stock Date" field.
  const [meterStartDate, setMeterStartDate] = useState('');
  useEffect(() => {
    api.meterReadings.getStartDate()
      .then(res => setMeterStartDate(res?.meter_start_date || ''))
      .catch(() => { /* non-fatal: backend defaults to Meter Start Date on save */ });
  }, []);
  const [puForm, setPuForm] = useState(EMPTY_PU);
  const [noForm, setNoForm] = useState(EMPTY_NO);
  const [cForm, setCForm] = useState(EMPTY_C);
  const [sForm, setSForm] = useState(EMPTY_S);

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  /* ── Products ── */
  const saveProduct = async () => {
    try {
      const res = await api.products.create(pForm);
      if (pForm.rate > 0) {
        await api.productRates.create({
          product_code: res.code || pForm.code,
          effective_date: new Date().toISOString().split('T')[0],
          rate: pForm.rate,
          tax_per: pForm.tax_per,
          remarks: 'Initial rate from Product Master'
        });
      }
      notify('✅ ' + tr('master.toasts.productSaved'));
      setPForm(EMPTY_P);
      setShowForm(false);
      products.reload();
    }
    catch (e) { notify('❌ ' + e.message); }
  };
  const updateProduct = async () => {
    try {
      await api.products.update(editRow.id, editRow);
      // Also create a rate history entry if rate is provided
      if (editRow.rate !== undefined && editRow.rate !== '' && parseFloat(editRow.rate) > 0) {
        await api.productRates.create({
          product_code: editRow.code,
          effective_date: editRow.effective_date || new Date().toISOString().split('T')[0],
          rate: editRow.rate,
          tax_per: editRow.tax_per,
          remarks: editRow.remarks || 'Updated from Product Master'
        });
      }
      notify('✅ ' + tr('master.toasts.productUpdated'));
      setEditRow(null);
      products.reload();
    }
    catch (e) { notify('❌ ' + e.message); }
  };
  const delProduct = async () => {
    try { await api.products.remove(confirm.row.id); notify('✅ ' + tr('master.toasts.deleted')); products.reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  /* ── Tanks ── */
  const saveTank = async () => {
    if (!tForm.tank_no || !tForm.product_code) { notify('❌ ' + tr('master.toasts.tankFieldsRequired')); return; }
    // Default the opening stock date to the Meter Start Date when the user left
    // it untouched. Blank is also fine — the backend defaults it server-side.
    const payload = { ...tForm, opening_stock_date: tForm.opening_stock_date || meterStartDate || '' };
    try { await api.tanks.create(payload); notify('✅ ' + tr('master.toasts.tankSaved')); setTForm(EMPTY_T); setShowForm(false); tanks.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const updateTank = async () => {
    // current_stock is the opening stock — read-only on edit and maintained only
    // by stock transactions. Strip it from the payload (backend ignores it too).
    const { current_stock, ...tankEdit } = editRow;
    try { await api.tanks.update(editRow.id, tankEdit); notify('✅ ' + tr('master.toasts.tankUpdated')); setEditRow(null); tanks.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const delTank = async () => {
    try { await api.tanks.remove(confirm.row.id); notify('✅ ' + tr('master.toasts.deleted')); tanks.reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  /* ── Pumps ── */
  const savePump = async () => {
    if (!puForm.name) { notify('❌ ' + tr('master.toasts.nameRequired')); return; }  // Primary Product removed per request
    try { await api.pumps.create({ ...puForm, nozzles: parseInt(puForm.nozzles) }); notify('✅ ' + tr('master.toasts.pumpAdded')); setPuForm(EMPTY_PU); setShowForm(false); pumps.reload(); }

    catch (e) { notify('❌ ' + e.message); }
  };
  const updatePump = async () => {
    try {
      await api.pumps.update(editRow.id, {
        name: editRow.name,
        product_code: editRow.product_code,
        nozzles: parseInt(editRow.nozzles),
        status: editRow.status,
        last_reading: parseFloat(editRow.last_reading || 0)
      });

      notify('✅ ' + tr('master.toasts.pumpUpdated')); setEditRow(null); pumps.reload();
    } catch (e) { notify('❌ ' + e.message); }
  };
  const delPump = async () => {
    try { await api.pumps.remove(confirm.row.id); notify('✅ ' + tr('master.toasts.pumpDeleted')); pumps.reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };
  const togglePumpStatus = async (pump) => {
    const newStatus = pump.status === 'Active' ? 'Inactive' : 'Active';
    try {
      await api.pumps.toggleStatus(pump.id, newStatus);
      pumps.reload();
      notify('✅ ' + tr('master.toasts.statusUpdated'));
    } catch (e) { notify('❌ ' + e.message); }
  };

  const toggleTankStatus = async (t) => {
    try {
      await api.tanks.toggleStatus(t.id, Number(t.is_active) ? 0 : 1);
      tanks.reload();
      notify('✅ ' + tr('master.toasts.statusUpdated'));
    } catch (e) { notify('❌ ' + e.message); }
  };

  const toggleNozzleStatus = async (n) => {
    try {
      await api.nozzles.toggleStatus(n.id, Number(n.is_active) ? 0 : 1);
      nozzles.reload();
      notify('✅ ' + tr('master.toasts.statusUpdated'));
    } catch (e) { notify('❌ ' + e.message); }
  };

  /* ── Nozzles ── */
  const saveNozzle = async () => {
    try {
      if (editRow && tab === 'Nozzles') {
        await api.nozzles.update(editRow.id, noForm);
        notify('✅ ' + tr('master.toasts.nozzleUpdated'));
        setEditRow(null);
      } else {
        await api.nozzles.create(noForm);
        notify('✅ ' + tr('master.toasts.nozzleCreated'));
      }
      setNoForm(EMPTY_NO); setShowForm(false); nozzles.reload();
    } catch (e) { notify('❌ ' + e.message); }
  };
  const delNozzle = async () => {
    try {
      await api.nozzles.remove(confirmNozzle.id);
      notify('✅ ' + tr('master.toasts.nozzleDeleted')); nozzles.reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirmNozzle(null);
  };

  /* ── Customers ── */
  const saveCustomer = async () => {
    try { await api.customers.create(cForm); notify('✅ ' + tr('master.toasts.customerSaved')); setCForm(EMPTY_C); setShowForm(false); customers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const updateCustomer = async () => {
    try { await api.customers.update(editRow.id, editRow); notify('✅ ' + tr('master.toasts.customerUpdated')); setEditRow(null); customers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const delCustomer = async () => {
    try { await api.customers.remove(confirm.row.id); notify('✅ ' + tr('master.toasts.deleted')); customers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  /* ── Suppliers ── */
  const saveSupplier = async () => {
    try { await api.suppliers.create(sForm); notify('✅ ' + tr('master.toasts.supplierSaved')); setSForm(EMPTY_S); setShowForm(false); suppliers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const updateSupplier = async () => {
    try { await api.suppliers.update(editRow.id, editRow); notify('✅ ' + tr('master.toasts.supplierUpdated')); setEditRow(null); suppliers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const delSupplier = async () => {
    try { await api.suppliers.remove(confirm.row.id); notify('✅ ' + tr('master.toasts.deleted')); suppliers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const handleConfirmDelete = () => {
    if (!confirm) return;
    const map = { Products: delProduct, Tanks: delTank, Pumps: delPump, Customers: delCustomer, Suppliers: delSupplier };
    (map[confirm.entity] || (() => setConfirm(null)))();
  };

  const current = {
    Products: products, Tanks: tanks, Pumps: pumps, Nozzles: nozzles, Customers: customers, Suppliers: suppliers,
    Employees: { loading: false, error: null, data: employees }
  }[tab] || { loading: false, error: null, data: [] };

  const openEdit = (row) => {
    if (tab === 'Nozzles') {
      setNoForm({ nozzle_no: row.nozzle_no, pump_id: row.pump_id, tank_id: row.tank_id });
      setEditRow(row);
      setShowForm(true);
    } else {
      setEditRow({
        ...row,
        effective_date: new Date().toISOString().split('T')[0],
        remarks: ''
      });
      setShowForm(false);
    }
  };
  const openDel = (row, entity) => setConfirm({ row, entity });

  return (
    <div className="master-page">
      <div className="page-header">
        <h2 className="page-title">{tr('master.title')}</h2>
        <Can
          module={
            tab === 'Products' ? 'products' :
              tab === 'Tanks' ? 'tanks' :
                tab === 'Pumps' ? 'pumps' :
                  tab === 'Nozzles' ? 'pumps' :
                    tab === 'Customers' ? 'customers' :
                      'suppliers'
          }
          action="create"
        >
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setShowForm(!showForm);
              setEditRow(null);
            }}
          >
            ＋ {tr('master.addNew')}
          </button>
        </Can>
      </div>
      {msg && <div style={{ padding: '6px 12px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>{msg}</div>}

      <div className="master-tabs">
        {TABS.map(t => (
          <button key={t} className={`master-tab ${tab === t ? 'active' : ''}`}
            onClick={() => {
              setTab(t);
              setShowForm(false);
              setEditRow(null);
              setNoForm(EMPTY_NO);
              window.location.hash = t; // Sync URL
            }}>
            {t === 'Products' && '🛢️ '}{t === 'Tanks' && '🔵 '}{t === 'Pumps' && '⛽ '}{t === 'Nozzles' && '🔧 '}{t === 'Customers' && '👤 '}{t === 'Suppliers' && '🏭 '}{t === 'Employees' && '👷 '}{tr(`master.tabs.${t}`)}
          </button>
        ))}
      </div>

      {current.loading && <Spinner />}
      {current.error && <ErrBanner msg={current.error} />}

      {/* ════════════ ADD FORMS ════════════ */}
      {showForm && tab === 'Products' && (
        <div className="section-card">
          <div className="section-header">{tr('master.product.addTitle')}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('master.product.codeLabel')}</label><input className="form-control" value={pForm.code} onChange={e => setPForm({ ...pForm, code: e.target.value })} placeholder={tr('master.product.codePlaceholder')} /></div>
            <div className="form-group"><label>{tr('common.name')}</label><input className="form-control" value={pForm.name} onChange={e => setPForm({ ...pForm, name: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.product.hsnCode')}</label><input className="form-control" value={pForm.hsn_code} onChange={e => setPForm({ ...pForm, hsn_code: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.product.unit')}</label>
              <select className="form-control" value={pForm.unit} onChange={e => setPForm({ ...pForm, unit: e.target.value })}>
                <option>Ltr</option><option>Kg</option><option>Pcs</option>
              </select>
            </div>
            <div className="form-group"><label>{tr('master.product.taxPercent')}</label><input type="number" className="form-control" value={pForm.tax_per} onChange={e => setPForm({ ...pForm, tax_per: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.product.rateRupees')}</label><input type="number" className="form-control" value={pForm.rate} onChange={e => setPForm({ ...pForm, rate: e.target.value })} /></div>
            <div className="form-group"><label>{tr('common.type')}</label>
              <select className="form-control" value={pForm.type} onChange={e => setPForm({ ...pForm, type: e.target.value })}>
                <option value="Lubricant">{tr('master.product.typeLubricant')}</option><option value="Fuel">{tr('master.product.typeFuel')}</option>
              </select>
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={saveProduct}>💾 {tr('common.save')}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {showForm && tab === 'Pumps' && (
        <div className="section-card">
          <div className="section-header">{tr('master.pump.addTitle')}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('master.pump.pumpNoLabel')}</label><input className="form-control" value={puForm.pump_no} onChange={e => setPuForm({ ...puForm, pump_no: e.target.value })} placeholder={tr('master.pump.pumpNoPlaceholder')} /></div>
            <div className="form-group"><label>{tr('master.pump.pumpName')}</label><input className="form-control" value={puForm.name} onChange={e => setPuForm({ ...puForm, name: e.target.value })} placeholder={tr('master.pump.pumpNamePlaceholder')} /></div>
            <div className="form-group"><label>{tr('master.pump.nozzles')}</label><input type="number" className="form-control" value={puForm.nozzles} onChange={e => setPuForm({ ...puForm, nozzles: e.target.value })} min={1} max={8} /></div>
            {/* Primary Product removed per request — kept for reference
            <div className="form-group"><label>Primary Product</label>
              <select className="form-control" value={puForm.product_code} onChange={e => setPuForm({ ...puForm, product_code: e.target.value })}>
                <option value="">-- Select Product --</option>
                {(products.data || []).map(p => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
              </select>
            </div>
            */}
            <div className="form-group"><label>{tr('common.status')}</label>
              <select className="form-control" value={puForm.status} onChange={e => setPuForm({ ...puForm, status: e.target.value })}>
                <option>Active</option><option>Inactive</option>
              </select>
            </div>

          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={savePump}>💾 {tr('common.save')}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {showForm && tab === 'Tanks' && (
        <div className="section-card">
          <div className="section-header">{tr('master.tank.addTitle')}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('master.tank.tankNo')}</label><input className="form-control" value={tForm.tank_no} onChange={e => setTForm({ ...tForm, tank_no: e.target.value })} placeholder={tr('master.tank.tankNoPlaceholder')} /></div>
            <div className="form-group"><label>{tr('common.product')}</label>
              <select className="form-control" value={tForm.product_code} onChange={e => setTForm({ ...tForm, product_code: e.target.value })}>
                <option value="">{tr('master.selectProduct')}</option>
                {(products.data || []).map(p => (
                  <option key={p.code} value={p.code}>{p.name} ({p.code})</option>
                ))}
              </select>
            </div>
            <div className="form-group"><label>{tr('master.tank.capacity')}</label><input type="number" className="form-control" value={tForm.capacity} onChange={e => setTForm({ ...tForm, capacity: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.tank.openingStock')}</label><input type="number" className="form-control" value={tForm.current_stock} onChange={e => setTForm({ ...tForm, current_stock: e.target.value })} title={tr('master.tank.openingStockTitle')} /></div>
            <div className="form-group"><label>{tr('master.tank.openingStockDate')}</label><input type="date" className="form-control" value={tForm.opening_stock_date || meterStartDate} onChange={e => setTForm({ ...tForm, opening_stock_date: e.target.value })} title={tr('master.tank.openingStockDateTitle')} /></div>
            <div className="form-group"><label>{tr('master.tank.minLevel')}</label><input type="number" className="form-control" value={tForm.min_level} onChange={e => setTForm({ ...tForm, min_level: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={saveTank}>💾 {tr('common.save')}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {showForm && tab === 'Nozzles' && (
        <div className="section-card">
          <div className="section-header">{editRow ? tr('master.nozzle.editTitle') : tr('master.nozzle.addTitle')}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('master.nozzle.nozzleNoLabel')}</label><input className="form-control" value={noForm.nozzle_no} onChange={e => setNoForm({ ...noForm, nozzle_no: e.target.value })} placeholder={tr('master.nozzle.nozzleNoPlaceholder')} /></div>
            <div className="form-group"><label>{tr('common.pump')}</label>
              <select className="form-control" value={noForm.pump_id} onChange={e => setNoForm({ ...noForm, pump_id: e.target.value })}>
                <option value="">{tr('master.selectPump')}</option>
                {(pumps.data || []).map(p => <option key={p.id} value={p.id}>{p.pump_no} ({p.name})</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tr('master.nozzle.tankSource')}</label>
              <select className="form-control" value={noForm.tank_id} onChange={e => setNoForm({ ...noForm, tank_id: e.target.value })}>
                <option value="">{tr('master.selectTank')}</option>
                {(tanks.data || []).filter(t => Number(t.is_active ?? 1) === 1).map(t => <option key={t.id} value={t.id}>{t.tank_no} — {t.product_code}</option>)}
              </select>
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={saveNozzle}>💾 {tr('master.nozzle.saveNozzle')}</button>
            <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditRow(null); setNoForm(EMPTY_NO); }}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {showForm && tab === 'Customers' && (
        <div className="section-card">
          <div className="section-header">{tr('master.customer.addTitle')}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('common.name')}</label><input className="form-control" value={cForm.name} onChange={e => setCForm({ ...cForm, name: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.gstin')}</label><input className="form-control" value={cForm.gstin} onChange={e => setCForm({ ...cForm, gstin: e.target.value })} /></div>
            <div className="form-group"><label>{tr('common.type')}</label>
              <select className="form-control" value={cForm.type} onChange={e => setCForm({ ...cForm, type: e.target.value })}>
                <option>Cash</option><option>Credit</option>
              </select>
            </div>
            <div className="form-group"><label>{tr('master.customer.creditLimit')}</label><input type="number" className="form-control" value={cForm.credit_limit} onChange={e => setCForm({ ...cForm, credit_limit: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.phone')}</label><input className="form-control" value={cForm.phone} onChange={e => setCForm({ ...cForm, phone: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={saveCustomer}>💾 {tr('common.save')}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {showForm && tab === 'Suppliers' && (
        <div className="section-card">
          <div className="section-header">{tr('master.supplier.addTitle')}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('common.name')}</label><input className="form-control" value={sForm.name} onChange={e => setSForm({ ...sForm, name: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.gstin')}</label><input className="form-control" value={sForm.gstin} onChange={e => setSForm({ ...sForm, gstin: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.supplier.contact')}</label><input className="form-control" value={sForm.contact} onChange={e => setSForm({ ...sForm, contact: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.supplier.productsSupplied')}</label><input className="form-control" value={sForm.product} onChange={e => setSForm({ ...sForm, product: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={saveSupplier}>💾 {tr('common.save')}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {/* ════════════ INLINE EDIT FORMS ════════════ */}
      {editRow && tab === 'Products' && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ {tr('master.product.editTitle')} — {editRow.code}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('common.name')}</label><input className="form-control" value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.product.hsnCode')}</label><input className="form-control" value={editRow.hsn_code || ''} onChange={e => setEditRow({ ...editRow, hsn_code: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.product.unit')}</label>
              <select className="form-control" value={editRow.unit} onChange={e => setEditRow({ ...editRow, unit: e.target.value })}>
                <option>Ltr</option><option>Kg</option><option>Pcs</option>
              </select>
            </div>
            <div className="form-group"><label>{tr('master.product.taxPercent')}</label><input type="number" className="form-control" value={editRow.tax_per} onChange={e => setEditRow({ ...editRow, tax_per: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.product.rateRequired')}</label><input type="number" className="form-control" value={editRow.rate} onChange={e => setEditRow({ ...editRow, rate: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.product.rateEffectiveFrom')}</label><input type="date" className="form-control" value={editRow.effective_date} onChange={e => setEditRow({ ...editRow, effective_date: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.product.rateRemarks')}</label><input className="form-control" value={editRow.remarks} placeholder={tr('master.product.rateRemarksPlaceholder')} onChange={e => setEditRow({ ...editRow, remarks: e.target.value })} /></div>
            <div className="form-group"><label>{tr('common.type')}</label>
              <select className="form-control" value={editRow.type} onChange={e => setEditRow({ ...editRow, type: e.target.value })}>
                <option value="Lubricant">{tr('master.product.typeLubricant')}</option><option value="Fuel">{tr('master.product.typeFuel')}</option>
              </select>
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={updateProduct}>💾 {tr('common.update')}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {editRow && tab === 'Pumps' && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ {tr('master.pump.editTitle')} — {editRow.pump_no}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('master.pump.pumpName')}</label><input className="form-control" value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.pump.nozzles')}</label><input type="number" className="form-control" value={editRow.nozzles} onChange={e => setEditRow({ ...editRow, nozzles: e.target.value })} min={1} max={8} /></div>
            {/* Primary Product removed per request — kept for reference
            <div className="form-group"><label>Primary Product</label>
              <select className="form-control" value={editRow.product_code} onChange={e => setEditRow({ ...editRow, product_code: e.target.value })}>
                <option value="">-- Select Product --</option>
                {(products.data || []).map(p => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
              </select>
            </div>
            */}
            <div className="form-group"><label>{tr('common.status')}</label>
              <select className="form-control" value={editRow.status} onChange={e => setEditRow({ ...editRow, status: e.target.value })}>
                <option>Active</option><option>Inactive</option>
              </select>
            </div>

            <div className="form-group"><label>{tr('master.pump.lastReading')}</label><input type="number" className="form-control" value={editRow.last_reading} onChange={e => setEditRow({ ...editRow, last_reading: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={updatePump}>💾 {tr('common.update')}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {editRow && tab === 'Tanks' && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ {tr('master.tank.editTitle')} — {editRow.tank_no}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('master.tank.tankNo')}</label><input className="form-control" value={editRow.tank_no || ''} onChange={e => setEditRow({ ...editRow, tank_no: e.target.value })} placeholder={tr('master.tank.tankNoPlaceholder')} /></div>
            <div className="form-group"><label>{tr('common.product')}</label>
              <select className="form-control" value={editRow.product_code || ''} onChange={e => setEditRow({ ...editRow, product_code: e.target.value })}>
                <option value="">{tr('master.selectProduct')}</option>
                {(() => {
                  const list = (products.data || []);
                  const cur = editRow.product_code;
                  const opts = list.map(p => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>);
                  // Keep the tank's existing product selectable even if it's a
                  // legacy code (MS/HSD/CNG) no longer in this station's master.
                  if (cur && !list.some(p => p.code === cur)) {
                    opts.unshift(<option key={cur} value={cur}>{cur}</option>);
                  }
                  return opts;
                })()}
              </select>
            </div>
            <div className="form-group"><label>{tr('master.tank.capacity')}</label><input type="number" className="form-control" value={editRow.capacity} onChange={e => setEditRow({ ...editRow, capacity: e.target.value })} /></div>
            {/* Current stock is the OPENING stock, set only at creation. On edit it is
                read-only — adjustments must go through Dip Reading, Fuel Balancing or
                Purchases so the stock ledger stays accurate (backend also ignores it). */}
            {/* Opening Stock is the immutable quantity entered at creation. Shown
                read-only here; the live current stock is in the Tank Master list
                and is maintained by Dip Reading, Fuel Balancing, and Purchases. */}
            <div className="form-group"><label>{tr('master.tank.openingStock')}</label><input type="number" className="form-control" value={editRow.opening_stock ?? editRow.current_stock} readOnly disabled title={tr('master.tank.openingStockImmutableTitle')} style={{ background: 'var(--bg-muted, #f1f5f9)', cursor: 'not-allowed' }} /><small style={{ color: 'var(--text-muted, #64748b)' }}>{tr('master.tank.openingStockHint')}</small></div>
            {/* Opening Stock Date is set once at creation; shown read-only here. */}
            <div className="form-group"><label>{tr('master.tank.openingStockDate')}</label><input type="date" className="form-control" value={editRow.opening_stock_date || meterStartDate || ''} readOnly disabled title={tr('master.tank.openingStockDateReadonlyTitle')} style={{ background: 'var(--bg-muted, #f1f5f9)', cursor: 'not-allowed' }} /></div>
            <div className="form-group"><label>{tr('master.tank.minLevel')}</label><input type="number" className="form-control" value={editRow.min_level} onChange={e => setEditRow({ ...editRow, min_level: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={updateTank}>💾 {tr('common.update')}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {editRow && tab === 'Customers' && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ {tr('master.customer.editTitle')} — {editRow.name}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('common.name')}</label><input className="form-control" value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.gstin')}</label><input className="form-control" value={editRow.gstin || ''} onChange={e => setEditRow({ ...editRow, gstin: e.target.value })} /></div>
            <div className="form-group"><label>{tr('common.type')}</label>
              <select className="form-control" value={editRow.type} onChange={e => setEditRow({ ...editRow, type: e.target.value })}>
                <option>Cash</option><option>Credit</option>
              </select>
            </div>
            <div className="form-group"><label>{tr('master.customer.creditLimit')}</label><input type="number" className="form-control" value={editRow.credit_limit} onChange={e => setEditRow({ ...editRow, credit_limit: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.phone')}</label><input className="form-control" value={editRow.phone || ''} onChange={e => setEditRow({ ...editRow, phone: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={updateCustomer}>💾 {tr('common.update')}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {editRow && tab === 'Suppliers' && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ {tr('master.supplier.editTitle')} — {editRow.name}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('common.name')}</label><input className="form-control" value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.gstin')}</label><input className="form-control" value={editRow.gstin || ''} onChange={e => setEditRow({ ...editRow, gstin: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.supplier.contact')}</label><input className="form-control" value={editRow.contact || ''} onChange={e => setEditRow({ ...editRow, contact: e.target.value })} /></div>
            <div className="form-group"><label>{tr('master.supplier.productsSupplied')}</label><input className="form-control" value={editRow.product || ''} onChange={e => setEditRow({ ...editRow, product: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={updateSupplier}>💾 {tr('common.update')}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {/* ════════════ PRODUCTS TABLE ════════════ */}
      {!current.loading && tab === 'Products' && (
        <div className="section-card">
          <div className="section-header">{tr('master.product.masterTitle')} ({(products.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>{tr('common.code')}</th>
                <th style={{ textAlign: 'left' }}>{tr('master.product.productName')}</th><th style={{ textAlign: 'left' }}>{tr('master.product.hsn')}</th>
                <th style={{ textAlign: 'left' }}>{tr('master.product.unit')}</th><th style={{ textAlign: 'left' }}>{tr('common.type')}</th><th>{tr('master.product.taxPercent')}</th><th>{tr('master.product.rateRupees')}</th>
                <th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>
              </tr></thead>
              <tbody>
                {(products.data || []).map((p, i) => (
                  <tr key={p.id}>
                    <td>{i + 1}</td>
                    <td><span className="badge badge-primary">{p.code}</span></td>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td style={{ fontFamily: 'monospace' }}>{p.hsn_code}</td>
                    <td>{p.unit}</td>
                    <td><span className={`badge ${p.type === 'Fuel' ? 'badge-danger' : 'badge-success'}`}>{p.type}</span></td>
                    <td className="right-align">{p.tax_per}%</td>
                    <td className="right-align">
                      ₹{fmtNum(p.rate)}
                      <button style={{ ...btnEdit, marginLeft: 8, padding: '2px 5px', fontSize: 10 }} title={tr('master.product.rateHistory')} onClick={() => setHistoryCode(p.code)}>🕒</button>
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Can module="products" action="update">
                        <button style={btnEdit} title={tr('common.edit')} onClick={() => openEdit(p)}>✏️</button>
                      </Can>
                      <Can module="products" action="delete">
                        <button style={btnDel} title={tr('common.delete')} onClick={() => openDel(p, 'Products')}>🗑️</button>
                      </Can>                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ PUMPS TABLE ════════════ */}
      {!current.loading && tab === 'Pumps' && (
        <div className="section-card">
          <div className="section-header">{tr('master.pump.masterTitle')} ({(pumps.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th>#</th><th style={{ textAlign: 'left' }}>{tr('master.pump.pumpNo')}</th><th style={{ textAlign: 'left' }}>{tr('common.name')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.product')}</th>
                <th style={{ textAlign: 'right' }}>{tr('master.pump.nozzles')}</th><th style={{ textAlign: 'right' }}>{tr('master.pump.lastReading')}</th><th style={{ textAlign: 'center' }}>{tr('common.status')}</th>
                <th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>

              </tr></thead>
              <tbody>
                {(pumps.data || []).map((p, i) => (
                  <tr key={p.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{p.pump_no}</td>
                    <td>{p.name}</td>
                    <td><span className="badge badge-info">{p.product_code}</span></td>
                    <td className="right-align">{p.nozzles}</td>
                    <td className="right-align">{fmtNum(p.last_reading)}</td>

                    <td style={{ textAlign: 'center' }}>
                      <button className={`badge ${p.status === 'Active' ? 'badge-success' : 'badge-danger'}`}
                        style={{ border: 'none', cursor: 'pointer', outline: 'none' }}
                        onClick={() => togglePumpStatus(p)}>
                        {p.status}
                      </button>
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Can module="pumps" action="update">
                        <button style={btnEdit} title={tr('common.edit')} onClick={() => openEdit(p)}>✏️</button>
                      </Can>

                      <Can module="pumps" action="delete">
                        {Number(p.has_transactions) ? (
                          <span title={tr('master.lockedDeleteTitle')} style={{ opacity: 0.45, marginLeft: 4, cursor: 'not-allowed' }}>🔒</span>
                        ) : (
                          <button style={btnDel} title={tr('common.delete')} onClick={() => openDel(p, 'Pumps')}>🗑️</button>
                        )}
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ NOZZLES TABLE ════════════ */}
      {!current.loading && tab === 'Nozzles' && (
        <div className="section-card">
          <div className="section-header">{tr('master.nozzle.masterTitle')} ({(nozzles.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>#</th>
                  <th style={{ textAlign: 'left' }}>{tr('master.nozzle.nozzleNo')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.pump')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('master.nozzle.tankSourceShort')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.product')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('master.nozzle.currentRate')}</th>
                  <th style={{ textAlign: 'center' }}>{tr('common.status')}</th>
                  <th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {(nozzles.data || []).length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>{tr('master.nozzle.noNozzles')}</td></tr>
                ) : (nozzles.data || []).map((n, i) => (
                  <tr key={n.id} style={Number(n.is_active ?? 1) ? undefined : { opacity: 0.5 }}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)' }}>{n.nozzle_no}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{n.pump_no}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{n.pump_name}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{n.tank_no}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{n.product_name}</div>
                    </td>
                    <td>
                      <span className={`badge ${n.product_code === 'MS' ? 'badge-danger' : n.product_code === 'HSD' ? 'badge-info' : 'badge-success'}`}>
                        {n.product_code}
                      </span>
                    </td>
                    <td className="right-align">
                      ₹{fmtNum(n.current_product_rate ?? n.product_rate ?? 0)} / {n.unit || 'Ltr'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Can module="pumps" action="update" fallback={<span className={`badge ${Number(n.is_active ?? 1) ? 'badge-success' : 'badge-danger'}`}>{Number(n.is_active ?? 1) ? tr('common.active') : tr('common.inactive')}</span>}>
                        <button className={`badge ${Number(n.is_active ?? 1) ? 'badge-success' : 'badge-danger'}`}
                          style={{ border: 'none', cursor: 'pointer', outline: 'none' }}
                          title={tr('master.toggleActiveInactive')}
                          onClick={() => toggleNozzleStatus(n)}>
                          {Number(n.is_active ?? 1) ? tr('common.active') : tr('common.inactive')}
                        </button>
                      </Can>
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Can module="pumps" action="update">
                        <button style={btnEdit} title={tr('common.edit')} onClick={() => openEdit(n)}>✏️</button>
                      </Can>

                      <Can module="pumps" action="delete">
                        {Number(n.has_transactions) ? (
                          <span title={tr('master.lockedDeleteTitle')} style={{ opacity: 0.45, marginLeft: 4, cursor: 'not-allowed' }}>🔒</span>
                        ) : (
                          <button style={btnDel} title={tr('common.delete')} onClick={() => setConfirmNozzle(n)}>🗑️</button>
                        )}
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ TANKS TABLE ════════════ */}
      {!current.loading && tab === 'Tanks' && (
        <>
        <div className="section-card">
          <div className="section-header">{tr('master.tank.masterTitle')} ({visibleTanks.length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>{tr('master.tank.tankNo')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.product')}</th><th>{tr('master.tank.capacity')}</th>
                <th>{tr('master.tank.currentStock')}</th><th>{tr('master.tank.minLevel')}</th><th>{tr('master.tank.fillPercent')}</th>
                <th style={{ textAlign: 'center' }}>{tr('common.status')}</th>
                <th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>
              </tr></thead>
              <tbody>
                {visibleTanks.map((t, i) => {
                  const pct = Math.round((Number(t.current_stock) / Number(t.capacity)) * 100) || 0;
                  return (
                    <tr key={t.id} style={Number(t.is_active ?? 1) ? undefined : { opacity: 0.5 }}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 700, color: 'var(--primary)' }}>{t.tank_no}</td>
                      <td><span className={`badge ${t.product_code === 'MS' ? 'badge-danger' : t.product_code === 'CNG' ? 'badge-success' : 'badge-info'}`}>{t.product_code}</span></td>
                      <td className="right-align">{fmtNum(t.capacity)}</td>
                      <td className="right-align">{fmtNum(t.current_stock)}</td>
                      <td className="right-align">{fmtNum(t.min_level)}</td>
                      <td className="right-align">
                        <div className="mini-progress"><div style={{ width: `${pct}%`, background: pct < 20 ? 'var(--danger)' : 'var(--secondary)' }} /></div>
                        {pct}%
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Can module="tanks" action="update" fallback={<span className={`badge ${Number(t.is_active ?? 1) ? 'badge-success' : 'badge-danger'}`}>{Number(t.is_active ?? 1) ? tr('common.active') : tr('common.inactive')}</span>}>
                          <button className={`badge ${Number(t.is_active ?? 1) ? 'badge-success' : 'badge-danger'}`}
                            style={{ border: 'none', cursor: 'pointer', outline: 'none' }}
                            title={tr('master.toggleActiveInactive')}
                            onClick={() => toggleTankStatus(t)}>
                            {Number(t.is_active ?? 1) ? tr('common.active') : tr('common.inactive')}
                          </button>
                        </Can>
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <Can module="tanks" action="update">
                          <button style={btnEdit} title={tr('common.edit')} onClick={() => openEdit(t)}>✏️</button>
                        </Can>

                        <Can module="tanks" action="delete">
                          {Number(t.has_transactions) ? (
                            <span title={tr('master.lockedDeleteTitle')} style={{ opacity: 0.45, marginLeft: 4, cursor: 'not-allowed' }}>🔒</span>
                          ) : (
                            <button style={btnDel} title={tr('common.delete')} onClick={() => openDel(t, 'Tanks')}>🗑️</button>
                          )}
                        </Can>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <TankLedger tanks={visibleTanks} />
        </>
      )}

      {/* ════════════ CUSTOMERS TABLE ════════════ */}
      {!current.loading && tab === 'Customers' && (
        <div className="section-card">
          <div className="section-header">{tr('master.customer.masterTitle')} ({(customers.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>{tr('common.name')}</th>
                <th style={{ textAlign: 'left' }}>{tr('master.gstin')}</th><th>{tr('master.customer.creditLimitShort')}</th>
                <th>{tr('common.balance')}</th><th style={{ textAlign: 'left' }}>{tr('common.type')}</th>
                <th style={{ textAlign: 'left' }}>{tr('master.phone')}</th><th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>
              </tr></thead>
              <tbody>
                {(customers.data || []).map((c, i) => (
                  <tr key={c.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '11.5px' }}>{c.gstin || '—'}</td>
                    <td className="right-align">₹{fmtNum(c.credit_limit)}</td>
                    <td className="right-align" style={{ color: Number(c.balance) > 0 ? 'var(--danger)' : 'var(--secondary)' }}>₹{fmtNum(c.balance)}</td>
                    <td><span className={`badge ${c.type === 'Credit' ? 'badge-warning' : 'badge-success'}`}>{c.type}</span></td>
                    <td>{c.phone || '—'}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Can module="customers" action="update">
                        <button style={btnEdit} title={tr('common.edit')} onClick={() => openEdit(c)}>✏️</button>
                      </Can>

                      <Can module="customers" action="delete">
                        <button style={btnDel} title={tr('common.delete')} onClick={() => openDel(c, 'Customers')}>🗑️</button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ SUPPLIERS TABLE ════════════ */}
      {!current.loading && tab === 'Suppliers' && (
        <div className="section-card">
          <div className="section-header">{tr('master.supplier.masterTitle')} ({(suppliers.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>{tr('common.name')}</th>
                <th style={{ textAlign: 'left' }}>{tr('master.gstin')}</th><th style={{ textAlign: 'left' }}>{tr('master.supplier.contact')}</th>
                <th style={{ textAlign: 'left' }}>{tr('master.supplier.products')}</th><th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>
              </tr></thead>
              <tbody>
                {(suppliers.data || []).map((s, i) => (
                  <tr key={s.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '11.5px' }}>{s.gstin}</td>
                    <td>{s.contact}</td>
                    <td><span className="badge badge-info">{s.product}</span></td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Can module="suppliers" action="update">
                        <button style={btnEdit} title={tr('common.edit')} onClick={() => openEdit(s)}>✏️</button>
                      </Can>

                      <Can module="suppliers" action="delete">
                        <button style={btnDel} title={tr('common.delete')} onClick={() => openDel(s, 'Suppliers')}>🗑️</button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ EMPLOYEES TABLE ════════════ */}
      {!current.loading && tab === 'Employees' && (
        <div className="section-card">
          <div className="section-header">{tr('master.employee.masterTitle')}</div>
          {showForm && (
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
              <div className="form-row">
                <div className="form-group"><label>{tr('common.name')}</label><input className="form-control" id="emp_name" placeholder={tr('master.employee.fullName')} /></div>
                <div className="form-group"><label>{tr('master.employee.role')}</label>
                  <select className="form-control" id="emp_role">
                    <option>Manager</option><option>Attendant</option><option>Cashier</option>
                    <option>Security</option><option>Mechanic</option><option>Supervisor</option>
                  </select>
                </div>
                <div className="form-group"><label>{tr('master.phone')}</label><input className="form-control" id="emp_phone" placeholder={tr('master.employee.phonePlaceholder')} /></div>
                <div className="form-group"><label>{tr('master.employee.joinDate')}</label><input type="date" className="form-control" id="emp_date" /></div>
                <div className="form-group"><label>{tr('master.employee.salary')}</label><input type="number" className="form-control" id="emp_salary" placeholder="0" /></div>
              </div>
              <div className="action-bar" style={{ padding: '0 0 10px' }}>
                <button className="btn btn-primary" onClick={() => {
                  const name = document.getElementById('emp_name').value;
                  const role = document.getElementById('emp_role').value;
                  const phone = document.getElementById('emp_phone').value;
                  const date = document.getElementById('emp_date').value;
                  const salary = document.getElementById('emp_salary').value;
                  if (!name || !phone) { notify('❌ ' + tr('master.toasts.namePhoneRequired')); return; }
                  setEmployees(prev => [...prev, { id: Date.now(), name, role, phone, join_date: date, salary: parseFloat(salary) || 0, status: 'Active' }]);
                  notify('✅ ' + tr('master.toasts.employeeSaved')); setShowForm(false);
                }}>💾 {tr('common.save')}</button>
                <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ {tr('common.cancel')}</button>
              </div>
            </div>
          )}
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>{tr('common.name')}</th>
                <th style={{ textAlign: 'left' }}>{tr('master.employee.role')}</th><th style={{ textAlign: 'left' }}>{tr('master.phone')}</th>
                <th style={{ textAlign: 'left' }}>{tr('master.employee.joinDate')}</th><th>{tr('master.employee.salary')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.status')}</th><th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>
              </tr></thead>
              <tbody>
                {employees.map((e, i) => (
                  <tr key={e.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{e.name}</td>
                    <td><span className="badge badge-info">{e.role}</span></td>
                    <td>{e.phone}</td>
                    <td>{e.join_date || '—'}</td>
                    <td className="right-align">₹{fmtNum(e.salary)}</td>
                    <td><span className="badge badge-success">{e.status}</span></td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button style={btnDel} onClick={() => setEmployees(prev => prev.filter(x => x.id !== e.id))}>🗑️</button>
                    </td>
                  </tr>
                ))}
                {employees.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>{tr('master.employee.noEmployees')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmNozzle}
        title={tr('master.nozzle.deleteTitle')}
        message={tr('master.nozzle.deleteMessage', { no: confirmNozzle?.nozzle_no })}
        onConfirm={delNozzle}
        onCancel={() => setConfirmNozzle(null)}
      />

      <ConfirmModal
        isOpen={!!confirm}
        title={tr('master.deleteEntityTitle', { entity: confirm?.entity?.replace(/s$/, '') || tr('master.record') })}
        message={tr('master.deleteEntityMessage', { name: confirm?.row?.name || confirm?.row?.code || confirm?.row?.tank_no || confirm?.row?.pump_no })}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirm(null)}
      />

      {historyCode && (
        <RateHistoryModal
          code={historyCode}
          onClose={() => setHistoryCode(null)}
          onDeleteClick={(rate) => setConfirmDeleteRate(rate)}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmDeleteRate}
        title={tr('master.product.deleteRateTitle')}
        message={confirmDeleteRate
          ? tr('master.product.deleteRateMessage', { rate: confirmDeleteRate.rate, date: confirmDeleteRate.effective_date })
          : ''}
        onConfirm={async () => {
          try {
            await api.productRates.remove(confirmDeleteRate.id);
            notify('✅ ' + tr('master.toasts.rateEntryDeleted'));
            setConfirmDeleteRate(null);
            setHistoryCode(null);
            products.reload();
          } catch (e) { notify('❌ ' + e.message); }
        }}
        onCancel={() => setConfirmDeleteRate(null)}
        confirmLabel={tr('common.delete')}
      />
    </div>
  );
}

function RateHistoryModal({ code, onClose, onDeleteClick }) {
  const { t: tr } = useTranslation();
  const { data, loading } = useApi(() => api.productRates.history(code), [code]);

  return (
    <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content" style={{ background: 'white', padding: 20, borderRadius: 8, width: '90%', maxWidth: 600, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', marginBottom: 15, borderBottom: '1px solid #eee', paddingBottom: 10 }}>
          <h3 style={{ margin: 0 }}>{tr('master.product.rateHistory')} — {code}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', marginLeft: 'auto' }}>✕</button>
        </div>
        {loading ? <Spinner /> : (
          <div style={{ overflowY: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{tr('common.date')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('common.rate')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('master.product.taxPercent')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('common.remarks')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(data || []).map(r => (
                  <tr key={r.id}>
                    <td style={{ textAlign: 'left', fontWeight: 600 }}>{r.effective_date}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>
                      ₹ {Number(r.rate).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {Number(r.tax_per).toFixed(2)}%
                    </td>
                    <td style={{ textAlign: 'left', fontSize: 12 }}>{r.remarks || '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button style={{ ...btnDel, padding: '2px 5px' }} onClick={() => onDeleteClick(r)}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const btnEdit = { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };
const btnDel = { background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
